import { SxProps, Theme } from "@mui/system";
import {
    accentBar,
    brandColors,
    cardInteractive,
    eyebrowPill,
    fullBleed,
    gradients,
    hoverOnly,
    iconTilePrimary,
} from "../../../theme/mixins";

// --- Hero -----------------------------------------------------------------

export const getHeroSectionStyles = (theme: Theme): SxProps<Theme> => {
    return {
        ...fullBleed,
        background: gradients.heroWash,
        color: theme.palette.text.primary,
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
    };
};

/**
 * The hero when the clinic has uploaded a photograph behind it.
 *
 * The wash is kept and laid **over** the image rather than replaced by it. The hero's text is
 * theme-coloured -- dark on a light ground -- and the picture underneath is whatever a clinic
 * chose to upload, so without a scrim the headline's legibility would depend on how bright
 * that photograph happens to be. A translucent wash means the page cannot be made unreadable
 * by a bad upload, and a well-chosen photograph still reads through it.
 *
 * `background-attachment` is deliberately left alone: fixed attachment is what produces the
 * parallax effect, and it is also what iOS Safari has never implemented, so it would look
 * different on the half of a clinic's patients who are on a phone.
 */
export const getHeroSectionWithImageStyles = (theme: Theme, imageUrl: string): SxProps<Theme> => {
    return {
        ...fullBleed,
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.86), rgba(255, 255, 255, 0.86)), url("${imageUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: theme.palette.text.primary,
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
    };
};

export const heroLogoContainerStyles: SxProps<Theme> = {
    textAlign: 'center',
};

export const heroLogoSpacingStyles: SxProps<Theme> = {
    mt: 0,
    mb: 4,
};

export const logoWrapperStyles: SxProps<Theme> = {
    width: '100%',
    maxWidth: '600px',
    height: '100px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mx: 'auto',
};

export const logoImageStyles: React.CSSProperties = {
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    objectPosition: 'center',
    transform: 'scale(0.8)',
    transformOrigin: 'center center',
};

export const heroEyebrowStyles: SxProps<Theme> = eyebrowPill;

export const mainHeadlineStyles = (theme: Theme): SxProps<Theme> => {
    return {
        fontSize: { xs: '2rem', sm: '2.4rem', md: '2.9rem' },
        fontWeight: 700,
        mb: 3,
        color: theme.palette.text.primary,
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
    };
};

// Second headline line, picked out in brand blue.
export const headlineAccentStyles = (theme: Theme): SxProps<Theme> => {
    return {
        color: theme.palette.primary.main,
    };
};

export const subtitleStyles = (theme: Theme): SxProps<Theme> => {
    return {
        mb: 5,
        color: theme.palette.text.secondary,
        fontSize: { xs: '1.1rem', md: '1.25rem' },
        lineHeight: 1.6,
        fontWeight: 400,
        maxWidth: '700px',
        mx: 'auto',
    };
};

// Stand-ins for the hero's words while the clinic's page is on its way. Sized like the lines
// they replace, so the buttons below do not jump when the words arrive.
export const heroSkeletonEyebrowStyles: SxProps<Theme> = {
    mx: 'auto',
    mb: 2,
    borderRadius: 999,
};

export const heroSkeletonHeadlineStyles: SxProps<Theme> = {
    mx: 'auto',
    fontSize: { xs: '2rem', sm: '2.4rem', md: '2.9rem' },
    maxWidth: '100%',
};

export const heroSkeletonSubtitleStyles: SxProps<Theme> = {
    mx: 'auto',
    mt: 3,
    mb: 5,
    maxWidth: '100%',
};

export const ctaTextContainerStyles: SxProps<Theme> = {
    textAlign: 'center',
};

export const ctaButtonsContainerStyles: SxProps<Theme> = {
    display: 'flex',
    gap: 2,
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'center',
    alignItems: 'center',
    mb: 6,
};

// The theme already supplies the pill shape, weight and hover lift — these only
// set the sizing the hero needs.
export const getPrimaryCtaButtonStyles = (theme: Theme): SxProps<Theme> => {
    return {
        minWidth: '210px',
        fontSize: '1.1rem',
    };
};

export const getSignInButtonStyles = (theme: Theme): SxProps<Theme> => {
    return {
        minWidth: '210px',
        fontSize: '1.1rem',
    };
};

// The stats band's styles went with the band itself -- see LandingPage.tsx for why the
// figures it drew could not survive the move from one clinic's page to the platform's.

// --- Features -------------------------------------------------------------

export const featuresSectionStyles: SxProps<Theme> = {
    py: { xs: 8, md: 12 },
};

export const featuresHeaderContainerStyles: SxProps<Theme> = {
    textAlign: 'center',
    mb: 8,
};

export const featuresEyebrowStyles: SxProps<Theme> = eyebrowPill;

export const featuresHeadingStyles = (theme: Theme): SxProps<Theme> => {
    return {
        color: theme.palette.text.primary,
        fontSize: { xs: '2rem', md: '2.5rem' },
        fontWeight: 700,
    };
};

export const featuresSubheadingStyles: SxProps<Theme> = {
    color: 'text.secondary',
    maxWidth: 700,
    mx: 'auto',
    fontSize: '1.1rem',
    fontWeight: 400,
};

export const featuresGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
    gap: 4,
};

export const getFeatureCardStyles = (theme: Theme): SxProps<Theme> => {
    return {
        height: '100%',
        textAlign: 'center',
        overflow: 'hidden',
        ...cardInteractive,
    };
};

export const featureAccentBarStyles: SxProps<Theme> = accentBar;

export const cardContentNoPaddingStyles: SxProps<Theme> = {
    p: 4,
};

export const featureIconBoxStyles: SxProps<Theme> = {
    ...iconTilePrimary,
    mx: 'auto',
    mb: 3,
};

export const featureTitleStyles = (theme: Theme): SxProps<Theme> => {
    return {
        fontWeight: 600,
        color: theme.palette.text.primary,
        mb: 1.5,
    };
};

export const featureDescriptionStyles: SxProps<Theme> = {
    lineHeight: 1.6,
};

export const getIconStyles = (theme: Theme): SxProps<Theme> => {
    return {
        fontSize: 32,
    };
};

// --- Closing CTA band -----------------------------------------------------

export const getCtaSectionStyles = (theme: Theme): SxProps<Theme> => {
    return {
        ...fullBleed,
        background: gradients.brandCta,
        color: 'white',
        py: { xs: 8, md: 11 },
    };
};

export const ctaTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    fontSize: { xs: '2rem', md: '2.75rem' },
    mb: 3,
};

export const ctaSubtitleStyles = (theme: Theme): SxProps<Theme> => {
    return {
        mb: 6,
        opacity: 0.92,
        maxWidth: '600px',
        mx: 'auto',
        fontSize: '1.2rem',
        fontWeight: 400,
        lineHeight: 1.6,
    };
};

export const ctaButtonsContainerLargeStyles: SxProps<Theme> = {
    display: 'flex',
    gap: 2,
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'center',
    alignItems: 'center',
};

export const getPrimaryCtaLargeButtonStyles = (theme: Theme): SxProps<Theme> => {
    return {
        minWidth: '250px',
        fontSize: '1.15rem',
    };
};

export const getSecondaryCtaOutlinedWhiteStyles = (theme: Theme): SxProps<Theme> => {
    return {
        minWidth: '200px',
        fontSize: '1.15rem',
        borderColor: 'rgba(255,255,255,0.7)',
        color: 'white',
        ...hoverOnly({
            borderColor: brandColors.secondary,
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
        }),
    };
};
